import { useEffect, useMemo, useRef, useState } from 'react';
import { hasPermission } from '@/types';
import { NavLink } from 'react-router-dom';
import { employees as api } from '@/api/endpoints';
import type { Employee, DwuConfirmRow } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';
import { validateUploadSize } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

const DNI = ['Pon.', 'Wt.', 'Śr.', 'Czw.', 'Pt.', 'Sob.', 'Niedz.'];

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (t?: string | null) => (t ? t.slice(0, 5) : '');

/** Poniedziałek tygodnia zawierającego podaną datę. */
function mondayOf(d: Date): Date {
  const off = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - off);
}

function openDownload(url: string) {
  const a = document.createElement('a');
  a.href = url; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
}

/** Godziny między HH:MM–HH:MM (do podglądu). */
function hoursBetween(start: string, end: string): string {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) return '';
  return (mins / 60).toFixed(2).replace('.', ',');
}

interface EditRow {
  date: string; dayName: string;
  startTime: string; endTime: string;
  delegation: number; location: string; notes: string;
}

/**
 * Dwutygodniówka: generator pustego/wypełnionego grafiku (XLSX/PDF) ORAZ uzupełnianie ze ZDJĘCIA —
 * AI odczytuje wypełnioną tabelę, wartości są edytowalne, a po zatwierdzeniu zastępują wpisy ewidencji
 * pracownika (→ zliczanie godzin, PODSUMOWANIE/pensja).
 */
export function DwutygodniowkaPage() {
  const { user } = useAuth();
  const canFill = hasPermission(user, 'HR_TIME_EDIT');

  const [people, setPeople] = useState<Employee[]>([]);
  const [employeeId, setEmployeeId] = useState<number | ''>('');
  const [startMonday, setStartMonday] = useState(ymd(mondayOf(new Date())));
  const [fromCalendar, setFromCalendar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ── Uzupełnianie ze zdjęcia ──
  const [photo, setPhoto] = useState<File | null>(null);
  const [editRows, setEditRows] = useState<EditRow[] | null>(null);
  const [extractInfo, setExtractInfo] = useState<{ provider: string; method: string; rowsWithData: number } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null); // null = brak wysyłki
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { api.list('ACTIVE').then(setPeople).catch(e => setErr(e?.message ?? String(e))); }, []);

  const rows = useMemo(() => {
    const out: { day: string; date: string }[] = [];
    const [y, m, d] = startMonday.split('-').map(Number);
    let cur = new Date(y, m - 1, d);
    while (out.length < 10) {
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6) out.push({ day: DNI[(dow + 6) % 7], date: ymd(cur) });
      cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    }
    return out;
  }, [startMonday]);

  const onDateChange = (v: string) => {
    if (!v) return;
    const [y, m, d] = v.split('-').map(Number);
    setStartMonday(ymd(mondayOf(new Date(y, m - 1, d))));
    setEditRows(null); setConfirmMsg(null);
  };

  const download = async (format: 'xlsx' | 'pdf') => {
    if (employeeId === '') { setErr('Wybierz pracownika'); return; }
    setBusy(true); setErr(null);
    try {
      if (fromCalendar) {
        const to = rows.length ? rows[rows.length - 1].date : startMonday;
        await api.prefillFromCalendar(Number(employeeId), startMonday, to);
      }
      const url = format === 'xlsx'
        ? api.dwutygodniowkaXlsxUrl(Number(employeeId), startMonday, fromCalendar)
        : api.dwutygodniowkaPdfUrl(Number(employeeId), startMonday, fromCalendar);
      openDownload(url);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const onExtract = async () => {
    if (employeeId === '') { setErr('Wybierz pracownika'); return; }
    if (!photo) { setErr('Wybierz zdjęcie dwutygodniówki'); return; }
    setAiBusy(true); setErr(null); setConfirmMsg(null);
    setUploadPct(0); // start wysyłki zdjęcia
    try {
      const ex = await api.dwutygodniowkaExtract(Number(employeeId), photo, startMonday, p => setUploadPct(p.percent));
      setEditRows(ex.rows.map(r => ({
        date: r.date, dayName: r.dayName,
        startTime: hhmm(r.startTime), endTime: hhmm(r.endTime),
        delegation: r.delegation ?? 0, location: r.location ?? '', notes: r.notes ?? '',
      })));
      setExtractInfo({ provider: ex.provider, method: ex.method, rowsWithData: ex.rowsWithData });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setAiBusy(false); setUploadPct(null); }
  };

  const onConfirm = async () => {
    if (!editRows || employeeId === '') return;
    setAiBusy(true); setErr(null);
    setUploadPct(0); // start wysyłki zdjęcia do archiwum
    try {
      const payloadRows: DwuConfirmRow[] = editRows.map(r => ({
        date: r.date,
        startTime: r.startTime || null, endTime: r.endTime || null,
        delegation: r.delegation, location: r.location || null, notes: r.notes || null,
      }));
      const res = await api.dwutygodniowkaConfirm(Number(employeeId), startMonday, payloadRows, photo, p => setUploadPct(p.percent));
      setConfirmMsg(`Zatwierdzono — ${res.created} dni zapisano w ewidencji pracownika.`);
      setEditRows(null); setPhoto(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setAiBusy(false); setUploadPct(null); }
  };

  const upd = (i: number, patch: Partial<EditRow>) =>
    setEditRows(rs => rs ? rs.map((r, idx) => idx === i ? { ...r, ...patch } : r) : rs);

  return (
    <div className="max-w-[1100px]">
      <header className="mb-5">
        <h1>Dwutygodniówka</h1>
        <div className="text-ink-400 text-[13px]">2-tygodniowy grafik godzin — generuj szablon albo uzupełnij ze zdjęcia (AI)</div>
      </header>

      <div className="card mb-4"><div className="card-body grid gap-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="field"><span className="field-label">Pracownik *</span>
            <SearchableSelect value={employeeId === '' ? '' : String(employeeId)} onChange={v => { setEmployeeId(v ? Number(v) : ''); setEditRows(null); setConfirmMsg(null); }}>
              <option value="">— wybierz —</option>
              {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
            </SearchableSelect></label>
          <label className="field"><span className="field-label">Poniedziałek startu</span>
            <input type="date" value={startMonday} onChange={e => onDateChange(e.target.value)} className="input"/></label>
          <label className="field"><span className="field-label">Źródło szablonu</span>
            <label className="flex items-center gap-2 text-[13px] text-ink-700 h-[38px]">
              <input type="checkbox" checked={fromCalendar} onChange={e => setFromCalendar(e.target.checked)} className="w-4 h-4"/>
              Wypełnij z Kalendarza brygad
            </label></label>
        </div>
        {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
        <div className="flex gap-2">
          <button onClick={() => download('xlsx')} disabled={busy} className="btn btn-secondary btn-sm"><Icon name="download" className="w-3.5 h-3.5"/> Szablon XLSX</button>
          <button onClick={() => download('pdf')} disabled={busy} className="btn btn-secondary btn-sm"><Icon name="download" className="w-3.5 h-3.5"/> Szablon PDF</button>
        </div>
      </div></div>

      {/* ── Uzupełnianie ze zdjęcia ── */}
      {canFill && (
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Uzupełnij ze zdjęcia</div>
              <div className="text-[11px] text-ink-400 mt-0.5">Wgraj zdjęcie wypełnionej tabeli — AI odczyta godziny; popraw i zatwierdź do ewidencji.</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <NavLink to="/dwutygodniowka-masowo" className="btn btn-secondary btn-sm"><Icon name="download" className="w-3.5 h-3.5"/> Wiele zdjęć / katalog</NavLink>
              <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => {
                const picked = e.target.files?.[0] ?? null;
                // Walidacja rozmiaru (limit 100 MB) — blokujemy zbyt duży plik
                const sizeErr = validateUploadSize(picked);
                if (sizeErr) { setErr(sizeErr); e.target.value = ''; return; }
                setPhoto(picked); setEditRows(null); setConfirmMsg(null);
              }}/>
              <button onClick={() => fileRef.current?.click()} className="btn btn-secondary btn-sm">
                <Icon name="plus" className="w-3.5 h-3.5"/> {photo ? 'Zmień zdjęcie' : 'Wybierz zdjęcie'}
              </button>
              <button onClick={onExtract} disabled={aiBusy || !photo} className="btn btn-primary btn-sm">
                <Icon name="sparkles" className="w-3.5 h-3.5"/> {aiBusy && !editRows ? 'Odczytywanie…' : 'Odczytaj ze zdjęcia'}
              </button>
            </div>
          </div>
          <div className="card-body">
            {photo && <div className="text-[12px] text-ink-500 mb-2">{photo.name} · {(photo.size / (1024 * 1024)).toFixed(1)} MB</div>}
            {uploadPct !== null && <div className="mb-2"><UploadProgressBar percent={uploadPct} label="Wysyłanie zdjęcia…"/></div>}
            {confirmMsg && (
              <div className="text-[13px] p-3 bg-green-50 border border-green-200 rounded text-green-700 flex items-center gap-2">
                <Icon name="check" className="w-4 h-4"/> {confirmMsg}
                {employeeId !== '' && <NavLink to={`/kadry/${employeeId}`} className="ml-1 underline text-green-700 hover:text-green-800">Zobacz ewidencję →</NavLink>}
              </div>
            )}
            {extractInfo && editRows && (
              <div className="text-[11px] text-ink-500 mb-2">
                Odczyt: <strong>{extractInfo.provider}</strong> ({extractInfo.method}) — {extractInfo.rowsWithData}/10 dni z danymi.
                {extractInfo.provider === 'mock' && <span className="text-amber-600 ml-1">Tryb mock — dane przykładowe; ustaw model AI w konfiguracji dla realnego odczytu.</span>}
                <span className="ml-1">Popraw wartości poniżej i zatwierdź.</span>
              </div>
            )}

            {editRows && (
              <>
                <div className="overflow-x-auto">
                  <table className="table-hw">
                    <thead><tr><th>Dzień</th><th>Data</th><th>Rozpoczęcie</th><th>Zakończenie</th><th>Godziny</th><th>Delegacja</th><th>Lokalizacja</th></tr></thead>
                    <tbody>
                      {editRows.map((r, i) => (
                        <tr key={r.date}>
                          <td className="text-[12px]">{r.dayName}</td>
                          <td className="font-mono text-[12px] text-ink-500">{r.date}</td>
                          <td><input type="time" value={r.startTime} onChange={e => upd(i, { startTime: e.target.value })} className="input !py-1 !px-2 w-[110px] font-mono text-[12px]"/></td>
                          <td><input type="time" value={r.endTime} onChange={e => upd(i, { endTime: e.target.value })} className="input !py-1 !px-2 w-[110px] font-mono text-[12px]"/></td>
                          <td className="font-mono text-[12px] text-ink-700 tabular-nums">{hoursBetween(r.startTime, r.endTime) || '—'}</td>
                          <td><input type="number" step="0.5" min="0" max="1" value={r.delegation} onChange={e => upd(i, { delegation: Number(e.target.value) })} className="input !py-1 !px-2 w-[70px] font-mono text-[12px] text-right"/></td>
                          <td><input value={r.location} onChange={e => upd(i, { location: e.target.value })} className="input !py-1 !px-2 text-[12px]" placeholder="np. Baza / teren"/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={onConfirm} disabled={aiBusy} className="btn btn-primary btn-sm">
                    <Icon name="check" className="w-3.5 h-3.5"/> {aiBusy ? 'Zapisywanie…' : 'Zatwierdź i zapisz w ewidencji'}
                  </button>
                  <button onClick={() => setEditRows(null)} disabled={aiBusy} className="btn btn-secondary btn-sm">Anuluj</button>
                  <span className="text-[11px] text-ink-400 ml-1">Zatwierdzenie zastępuje wpisy ewidencji dla tych 10 dni; zdjęcie trafia do archiwum pracownika.</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
